<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { Link } from '@inertiajs/vue3';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CustomLink from './CustomLink.vue';
import Button from './Button.vue'; // Add Button import

gsap.registerPlugin(ScrollTrigger);

const props = defineProps({
  products: {
    type: Array,
    required: true,
  },
  title: {
    type: String,
    default: "Slim, schaalbaar en krachtig.",
  },
  subtitle: {
    type: String,
    default: "Onze projecten laten zien hoe flexibele software echte resultaten oplevert – en hoe wij jou kunnen helpen om meer te verbinden, minder te verspillen en slimmer te werken."
  }
});

// Refs
const cardsContainerRef = ref(null);
const showcaseRef = ref(null);
const currentPage = ref(0);
const totalPages = ref(0);

// Calculate how many cards are visible based on container width
const visibleCards = ref(1);
const gapWidth = ref(32); // Default gap value (2rem = 32px)
const singleCardScrollWidth = ref(416); // 24rem + 2rem = 416px

// Change the scrolling logic to move by one card at a time
const scrollToCard = (index) => {
  if (!cardsContainerRef.value) return;

  // Ensure index is within bounds
  const maxIndex = props.products.length - visibleCards.value;
  const targetIndex = Math.max(0, Math.min(index, maxIndex));
  currentPage.value = targetIndex;

  // Get exact card width from DOM if possible
  const cardElement = cardsContainerRef.value.querySelector('.product-card');
  const actualCardWidth = cardElement ? cardElement.offsetWidth : 384; // 24rem = 384px
  
  // Get actual gap width from computed style
  const computedStyle = window.getComputedStyle(cardsContainerRef.value);
  const actualGap = parseInt(computedStyle.gap) || 32; // Default to 2rem (32px)
  
  // Calculate scroll position - fixed amount per card
  const scrollAmount = targetIndex * (actualCardWidth + actualGap);
  
  console.log(`Scrolling to card ${targetIndex}, amount: ${scrollAmount}px, cardWidth: ${actualCardWidth}px, gap: ${actualGap}px`);
  
  cardsContainerRef.value.scrollTo({
    left: scrollAmount,
    behavior: 'smooth'
  });
};

// Navigation functions - modified to move by one card at a time
const navigatePrev = () => {
  if (currentPage.value > 0) {
    scrollToCard(currentPage.value - 1);
  }
};

const navigateNext = () => {
  const maxIndex = props.products.length - visibleCards.value;
  if (currentPage.value < maxIndex) {
    scrollToCard(currentPage.value + 1);
  }
};

// Handle scroll event to update current card
const handleScroll = () => {
  if (!cardsContainerRef.value) return;
  
  const scrollPosition = cardsContainerRef.value.scrollLeft;
  
  // Determine current card based on scroll position
  const newIndex = Math.round(scrollPosition / singleCardScrollWidth.value);
  const maxIndex = props.products.length - visibleCards.value;
  
  if (newIndex !== currentPage.value) {
    currentPage.value = Math.min(newIndex, maxIndex);
  }
};

// Check if we're at the last possible card
const isLastCard = () => {
  return currentPage.value >= props.products.length - visibleCards.value;
};

// Debounce function
const debounce = (fn, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

// Simplify the recalculateLayout function
const recalculateLayout = async () => {
  if (!cardsContainerRef.value) return;
  
  // Wait for DOM updates
  await nextTick();
  
  // Determine how many cards fit based on screen width
  const screenWidth = window.innerWidth;
  
  if (screenWidth < 768) {
    visibleCards.value = 1; // Mobile: 1 card
  } else if (screenWidth < 1024) {
    visibleCards.value = 2; // md: 2 cards
  } else {
    visibleCards.value = 3; // lg: 3 cards
  }
  
  // Get actual gap width from computed style
  const computedStyle = window.getComputedStyle(cardsContainerRef.value);
  gapWidth.value = parseInt(computedStyle.gap) || 32; // Default to 2rem (32px)
  
  // Calculate total possible card positions for navigation
  totalPages.value = props.products.length - visibleCards.value + 1;
  
  // Ensure current card index is valid
  currentPage.value = Math.min(currentPage.value, props.products.length - visibleCards.value);
  
  // Update scroll position
  scrollToCard(currentPage.value);
};

// Handle window resize
const handleResize = debounce(() => {
  recalculateLayout();
}, 250);

// Add a ref to store selected images
const selectedImages = ref({});

// Helper function to get image for a product - prioritize sliderImage with new data structure
const getProductImage = (product) => {
  // First priority: use sliderImage if available
  if (product.media?.sliderImage) {
    return product.media.sliderImage;
  }
  
  // Second priority: fallback to showcase images
  if (product.sections?.showcase && Object.keys(product.sections.showcase).length > 0) {
    const showcaseKeys = Object.keys(product.sections.showcase);
    const randomKey = showcaseKeys[Math.floor(Math.random() * showcaseKeys.length)];
    return product.sections.showcase[randomKey].image;
  }
  
  // Third priority: use mockup image
  if (product.media?.mockupImages?.mid?.src) {
    return product.media.mockupImages.mid.src;
  }
  
  // Final fallback
  return 'https://images.unsplash.com/photo-1511376777868-611b54f68947';
};

// Initialize image selections once for all products
const initializeProductImages = () => {
  props.products.forEach(product => {
    // Store the selected image URL for each product by ID
    selectedImages.value[product.meta.id] = getProductImage(product);
  });
};

// Helper function to split text on commas and retain commas at the end of each line
const splitTextOnComma = (text) => {
  return text ? text.split(',').map((line, i, arr) => i < arr.length - 1 ? `${line.trim()},` : line.trim()) : [];
};

onMounted(() => {
  // Add resize event listener
  window.addEventListener('resize', handleResize);
  
  // Initial calculations
  recalculateLayout();
  
  // Initialize product images
  initializeProductImages();
  
  // Improved animation for cards with consistent staggering
  const cards = cardsContainerRef.value.querySelectorAll('.product-card');
  
  // Create a timeline for better control
  const cardsTl = gsap.timeline({
    scrollTrigger: { 
      trigger: showcaseRef.value,
      start: 'top 80%',
      toggleActions: "play none none none",
    }
  });
  
  // Add each card to the timeline with consistent delays
  cards.forEach((card, index) => {
    cardsTl.fromTo(card, 
      { 
        opacity: 0, 
        y: 80, 
        scale: 0,
        rotationX: 20,
        transformOrigin: "center bottom"
      }, 
      { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        rotationX: 0,
        duration: 0.7,
        ease: "back.out(1.4)",
      }, 
      index * 0.3 // Consistent 0.2s delay between each card
    );
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
    <!-- Showcase Cards Component -->  
    <div ref="showcaseRef" class="relative w-full overflow-visible">
        <div class="wide-container flex flex-col">
            <div class="h-full w-full min-h-[1024px]">
                <div class="flex flex-col lg:flex-row items-start lg:items-end lg:justify-between w-full mb-20 gap-4">
                    <!-- Title Section -->
                    <hgroup class="flex flex-col grow">
                        <h2 class="text-pretty text-5xl font-semibold text-jet sm:text-6xl lg:text-7xl tracking-tight">{{ title }}</h2>
                        <p class="text-balance text-left text-xl sm:font-medium text-jet/80 max-w-6xl mt-6">{{ subtitle }}</p>
                    </hgroup>
                    
                    <Button href="/products" variant="white" icon="arrow-right" :block="true">
                        Projecten vol slimme oplossingen
                    </Button>
                </div>

                <!-- Cards row with dynamic calculations -->
                <div ref="cardsContainerRef" @scroll="handleScroll" class="w-full flex flex-nowrap overflow-y-visible overflow-x-auto gap-8 scrollbar-hide [scrollbar-width:none] [-ms-overflow-style:none]">
                    <CustomLink v-for="(product, index) in products" :key="product.meta.id" :href="`/project/${product.meta.slug}`" :useInertia="true" class="opacity-0 product-card group/card flex flex-none rounded-xl p-6 relative overflow-hidden h-[40rem] w-96 transition-all duration-500 hover:scale-[1.02] shadow-sm hover:shadow-xl [transform-style:preserve-3d] ease-[cubic-bezier(.65,.05,.36,1.15)]" :class="{'snap-start': index === 0, 'snap-center': index > 0 && index < products.length - 1, 'snap-end': index === products.length - 1}">
                        <!-- Product Image -->
                        <img :src="product.media?.sliderImage || selectedImages[product.meta.id]" :alt="`Bekijk ${product.meta.name} - ${product.content.subTitle || product.content.description}`" class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 scale-105 group-hover/card:scale-110 ease-[cubic-bezier(.5,.85,.25,1.8)]" :class="[`bg-gradient-to-tr from-${product.style.projectColor}-200`, `to-${product.style.projectColor}-400`, `via-${product.style.projectColor}-300 via-45%` ]" />
    
                        <!-- Animated gradient overlay -->
                        <div :class="`absolute inset-0 bg-gradient-to-tr from-black/60 to-black/50 transition-all duration-1000 group-hover/card:opacity-25 scale-110 group-hover/card:scale-150]`"></div>
                        
                        <!-- Meer Info Overlay (New) -->
                        <div class="absolute inset-0 flex items-center justify-center opacity-0 scale-75 group-hover/card:opacity-100 group-hover/card:scale-100 transition-all duration-500 ease-[cubic-bezier(.5,.85,.25,1.8)] z-20">
                            <div class="flex flex-col items-center gap-3">
                                <font-awesome-icon :icon="['fas', 'search-plus']" class="h-8 w-8 text-base-linen" />
                                <span class="text-base-linen text-balance font-bold text-4xl sm:text-5xl text-center">{{product.content.hoverCta}}</span>
                            </div>
                        </div>
                        
                        <!-- Content -->
                        <div class="z-10 flex flex-col w-full h-full justify-between">
                            <div class="p-4 flex items-center justify-center">
                                <img :src="product.meta.logo" :alt="`${product.meta.name} logo`" class="aspect-video w-full h-full max-h-32 max-w-64 object-contain transition-all duration-500 group-hover/card:opacity-0 group-hover/card:delay-[50ms] ease-[cubic-bezier(.5,.85,.25,1.8)]" />
                            </div>

                            <div class="w-full flex flex-row justify-between text-center mb-4">
                                <p class="text-2xl text-base-linen w-full mt-auto font-bold text-balance transition-all duration-500 group-hover/card:text-base-linen/0 group-hover/card:delay-[50ms] ease-[cubic-bezier(.5,.85,.25,1.8)]">
                                    <span v-for="(line, i) in splitTextOnComma(product.content.subTitle || product.content.description)" :key="i">
                                        {{ line }}<br v-if="i < splitTextOnComma(product.content.subTitle || product.content.description).length - 1" />
                                    </span>
                                </p>
                            </div>
                        </div>
                    </CustomLink>
                </div>
                
                <!-- Navigation buttons -->
                <div class="mt-6 flex flex-row justify-center gap-3">
                    <button @click="navigatePrev" :disabled="currentPage === 0" 
                            :class="`h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 
                                    ${currentPage === 0 ? 'bg-base-dun/20 text-base-dun/40 cursor-not-allowed' : 'bg-base-jet text-base-linen hover:bg-base-walnut'}`" 
                            aria-label="Vorige kaart">
                        <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-4 w-4" />
                    </button>
                    
                    <button @click="navigateNext" :disabled="isLastCard()" 
                            :class="`h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 
                                    ${isLastCard() ? 'bg-base-dun/20 text-base-dun/40 cursor-not-allowed' : 'bg-base-jet text-base-linen hover:bg-base-walnut'}`" 
                            aria-label="Volgende kaart">
                        <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
